import React, { useState, useEffect } from 'react';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconClock,
  IconUsers,
  IconServer,
  IconBolt,
  IconCrown
} from '@tabler/icons-react';

const StatusTab = () => {
  const [status, setStatus] = useState({
    panel: 'checking',
    auth: 'checking',
    mainServer: 'checking',
  });
  
  const [stats, setStats] = useState({
    total_users: 0,
    total_tunnels: 0,
    active_tunnels: 0,
    pro_users: 0,
    games: []
  });

  // Загрузка статистики из БД
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${API_BASE_URL}/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.log('Ошибка загрузки статистики:', error);
      }
    };
    
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Обновляем каждые 30 сек
    return () => clearInterval(interval);
  }, []);

  // Проверить статусы сервисов
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
        const token = localStorage.getItem('access_token');
        
        // Проверка панели - проверяем доступность самой панели
        const panelCheck = fetch(window.location.href)
          .then(r => r.ok ? 'online' : 'offline')
          .catch(() => 'offline');

        // Проверка сервера авторизации - проверяем /auth/me endpoint
        const authCheck = fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
        })
          .then(r => r.ok ? 'online' : 'offline')
          .catch(() => 'offline');

        // Проверка основного сервера - проверяем через API туннелей
        const serverCheck = fetch(`${API_BASE_URL}/v1/tunnels`, {
          headers: {
            'Authorization': `Bearer ${token || ''}`
          },
          credentials: 'include',
        })
          .then(r => r.ok || r.status === 401 ? 'online' : 'offline')
          .catch(() => 'offline');

        const [panel, auth, mainServer] = await Promise.all([panelCheck, authCheck, serverCheck]);
        
        setStatus({ panel, auth, mainServer });
      } catch (error) {
        console.log('Ошибка при проверке статуса:', error);
        setStatus({ panel: 'offline', auth: 'offline', mainServer: 'offline' });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 10000); // Проверка каждые 10 сек
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' };
      case 'offline':
        return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' };
      case 'checking':
        return { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' };
      default:
        return { bg: 'bg-dark-800', border: 'border-dark-700', text: 'text-dark-400' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <IconCircleCheck className="w-5 h-5 text-green-400" />;
      case 'offline':
        return <IconAlertCircle className="w-5 h-5 text-red-400" />;
      case 'checking':
        return <IconClock className="w-5 h-5 text-yellow-400 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online':
        return 'Работает';
      case 'offline':
        return 'Не доступен';
      case 'checking':
        return 'Проверка...';
      default:
        return 'Неизвестно';
    }
  };

  const services = [
    { key: 'panel', name: 'Панель управления', description: 'Web интерфейс' },
    { key: 'auth', name: 'Сервер авторизации', description: 'Аутентификация пользователей' },
    { key: 'mainServer', name: 'Основной сервер', description: 'API и управление туннелями' },
  ];

  return (
    <div className="space-y-4">
      {/* Статистика из БД */}
      <div className="bg-white dark:bg-dark-900 border border-gray-300 dark:border-dark-800 rounded-xl p-4">
        <h3 className="text-gray-900 dark:text-white font-semibold mb-3">Статистика платформы</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gray-50 dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <IconUsers className="w-4 h-4 text-primary-500" />
              <span className="text-2xl font-bold text-primary-500">{stats.total_users}</span>
            </div>
            <div className="text-gray-600 dark:text-dark-600 text-xs">Пользователей</div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <IconServer className="w-4 h-4 text-primary-500" />
              <span className="text-2xl font-bold text-primary-500">{stats.total_tunnels}</span>
            </div>
            <div className="text-gray-600 dark:text-dark-600 text-xs">Туннелей</div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <IconBolt className="w-4 h-4 text-green-500" />
              <span className="text-2xl font-bold text-green-500">{stats.active_tunnels}</span>
            </div>
            <div className="text-gray-600 dark:text-dark-600 text-xs">Активных</div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <IconCrown className="w-4 h-4 text-yellow-500" />
              <span className="text-2xl font-bold text-yellow-500">{stats.pro_users}</span>
            </div>
            <div className="text-gray-600 dark:text-dark-600 text-xs">PRO</div>
          </div>
        </div>
      </div>

      {/* Список сервисов */}
      <div className="space-y-3">
        {services.map((service) => {
          const statusValue = status[service.key];
          const colors = getStatusColor(statusValue);
          
          return (
            <div
              key={service.key}
              className={`${colors.bg} border ${colors.border} rounded-xl p-4 flex items-start gap-4 transition-all`}
            >
              <div className="mt-1 flex-shrink-0">
                {getStatusIcon(statusValue)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-gray-900 dark:text-white font-semibold text-sm mb-0.5">{service.name}</h4>
                <p className="text-gray-600 dark:text-dark-500 text-xs">{service.description}</p>
              </div>
              <div className={`${colors.text} text-xs font-medium whitespace-nowrap flex-shrink-0`}>
                {getStatusText(statusValue)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Инструкции */}
      <div className="bg-white dark:bg-dark-900 border border-gray-300 dark:border-dark-800 rounded-xl p-4 text-sm">
        <p className="text-gray-600 dark:text-dark-600 mb-2">
          ℹ️ Если какой-то сервис не доступен, проверьте подключение к интернету.
        </p>
        <p className="text-gray-500 dark:text-dark-500 text-xs">
          Последняя проверка: {new Date().toLocaleTimeString('ru-RU')}
        </p>
      </div>
    </div>
  );
};

export default StatusTab;
